/**
 * Trip CO₂ Calculator — Methodology dialog (tabbed).
 *
 * A native `<dialog>` that surfaces the same methodology copy previously
 * rendered inline by `MethodologyPanel`, split into three tabs so the
 * long content set (Assumptions, Per-mode calculation, "Days of an
 * average person" comparison) is easier to skim.
 *
 * The trigger is a "Methodology 🔎" pill button intended to sit side-by-
 * side with the "Share these results" button below the Perspective
 * section — same shape and size, so the eye reads them as a peer pair.
 *
 * Content components (`AssumptionsContent`, `PerModeCalculationContent`,
 * `DaysOfAverageComparisonContent`) are imported from ResultChart so
 * the legacy `MethodologyPanel` and this dialog never drift in copy.
 */

import React, { useEffect, useRef, useState } from 'react';
import type { ChartRow } from './buildComparison';
import {
  AssumptionsContent,
  PerModeCalculationContent,
  DaysOfAverageComparisonContent,
} from './ResultChart';

export interface MethodologyDialogProps {
  /** User-trip rows — required for the Per-mode calculation tab. */
  rows: ChartRow[];
}

type TabId = 'assumptions' | 'per-mode' | 'days';

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: 'assumptions', label: 'Assumptions' },
  { id: 'per-mode', label: 'Per-mode calculation' },
  { id: 'days', label: '"Days of an average person"' },
];

const MethodologyDialog: React.FC<MethodologyDialogProps> = ({ rows }) => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('assumptions');

  // Close-on-backdrop-click — same pattern as ShareButton + CantTrainDialog.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onClick = (e: MouseEvent) => {
      if (e.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inside) dialog.close();
    };
    dialog.addEventListener('click', onClick);
    return () => dialog.removeEventListener('click', onClick);
  }, []);

  const open = () => {
    // Re-anchor to the first tab whenever the dialog opens, so a
    // returning user lands on the most important block by default.
    setActiveTab('assumptions');
    dialogRef.current?.showModal();
  };
  const close = () => dialogRef.current?.close();

  return (
    <>
      <MethodologyTriggerButton onClick={open} />

      <dialog
        ref={dialogRef}
        aria-labelledby="methodology-dialog-title"
        style={{
          padding: 0,
          border: 0,
          borderRadius: 14,
          width: 'min(640px, 94vw)',
          maxHeight: '88vh',
          background: '#fff',
          color: '#0f172a',
          boxShadow: '0 24px 56px rgba(15, 23, 42, 0.25)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.25rem 0.75rem',
            borderBottom: '1px solid #e2e8f0',
            position: 'relative',
          }}
        >
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              padding: '4px 8px',
              fontSize: 16,
              lineHeight: 1,
              background: 'transparent',
              border: 0,
              color: '#64748b',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <h3
            id="methodology-dialog-title"
            style={{
              fontSize: 18,
              margin: '0 0 0.75rem',
              letterSpacing: -0.2,
              lineHeight: 1.2,
            }}
          >
            Methodology 🔎
          </h3>

          {/* Tabs (role="tablist"). Horizontally scrollable on narrow
              viewports so tab labels never get truncated mid-word. */}
          <div
            role="tablist"
            aria-label="Methodology sections"
            style={{
              display: 'flex',
              gap: 4,
              overflowX: 'auto',
              marginBottom: -1, // overlap the bottom border for the active tab
            }}
          >
            {TABS.map((tab) => {
              const active = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={`methodology-panel-${tab.id}`}
                  id={`methodology-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '0.5rem 0.8rem',
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: active ? '#0f172a' : '#64748b',
                    background: 'transparent',
                    border: 0,
                    borderBottom: `2px solid ${active ? 'hsl(174, 70%, 43%)' : 'transparent'}`,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab panels — only the active one renders, scroll container
            keeps long content tame within the dialog's maxHeight. */}
        <div
          style={{
            padding: '1rem 1.25rem 1.25rem',
            overflowY: 'auto',
            fontSize: 13,
            color: '#475569',
            lineHeight: 1.55,
          }}
        >
          {activeTab === 'assumptions' && (
            <div
              role="tabpanel"
              id="methodology-panel-assumptions"
              aria-labelledby="methodology-tab-assumptions"
            >
              <AssumptionsContent />
            </div>
          )}
          {activeTab === 'per-mode' && (
            <div
              role="tabpanel"
              id="methodology-panel-per-mode"
              aria-labelledby="methodology-tab-per-mode"
            >
              <PerModeCalculationContent rows={rows} />
            </div>
          )}
          {activeTab === 'days' && (
            <div
              role="tabpanel"
              id="methodology-panel-days"
              aria-labelledby="methodology-tab-days"
            >
              <DaysOfAverageComparisonContent />
            </div>
          )}
        </div>
      </dialog>
    </>
  );
};

/**
 * Pill-shaped trigger button. Styled as a *secondary* peer to the
 * primary "Share these results" button — same shape and padding so
 * they line up cleanly, but neutral colours so the share CTA still
 * carries the visual weight.
 */
const MethodologyTriggerButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '0.7rem 1.1rem',
      fontSize: 14,
      fontWeight: 700,
      color: '#0f172a',
      background: '#fff',
      border: '1.5px solid #cbd5e1',
      borderRadius: 999,
      cursor: 'pointer',
      transition: 'border-color 0.12s ease, transform 0.12s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#94a3b8';
      e.currentTarget.style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#cbd5e1';
      e.currentTarget.style.transform = '';
    }}
  >
    <span>Methodology</span>
    <span aria-hidden>🔎</span>
  </button>
);

export default MethodologyDialog;
